import { matchPath } from 'react-router-dom';

// Admin permission catalog — single source of truth for the sidebar filter,
// route guard, and button gates. Codes below are the REAL backend codes as
// returned by GET /api/admin/users/info (`permissions`). See docs/ADMIN_PERMISSIONS.md.

// Highest role: holding it (active) overrides every gate. A ROLE, not a code —
// matched case-insensitively against a role `name` in users/info `roles`.
export const SUPER_ADMIN_ROLE = 'administrator_super';

// Category = the first segment of a code (backend requires `code` to start with
// `<category>.`). Used by the Permissions admin page's create/edit form.
export const PERMISSION_CATEGORIES = [
  'admin',
  'apps',
  'asset',
  'campaign',
  'claim',
  'cms',
  'content',
  'database',
  'entity',
  'feedback',
  'learn',
  'location',
  'marketing',
  'menu',
  'message',
  'messaging',
  'organization',
  'payment',
  'promotion',
  'report',
  'support',
  'integration',
  'scheduler',
  'cloud',
  'workers',
  'ratelimit',
  'voice',
  'system',
  'tag',
  'user',
  'voucher',
  'whatsapp',
  'workflow',
  'sales',
] as const;

export type PermissionCategory = (typeof PERMISSION_CATEGORIES)[number];

// Real permission codes, grouped by domain. Only the codes actually referenced
// by routes and button gates are listed; add more from the registry as needed.
export const PERMISSIONS = {
  user: {
    view: 'user.view',
    edit: 'user.edit',
    delete: 'user.delete',
    roleAssign: 'user.role.assign',
    roleRevoke: 'user.role.revoke',
  },
  // POST /api/admin/user_role enforces this, not user.role.assign.
  role: { create: 'role.create' },
  // Human Resource (ff-api 0477). Own-record reads need neither.
  hr: { view: 'hr.view', manage: 'hr.manage', leaveApprove: 'hr.leave.approve' },
  comp: { view: 'comp.view', manage: 'comp.manage', approve: 'comp.approve' },
  perf: { view: 'perf.view', manage: 'perf.manage' },
  accounting: { view: 'accounting.view', manage: 'accounting.manage' },
  payroll: { view: 'payroll.view', manage: 'payroll.manage' },
  // Managing the permission registry / direct user permissions.
  systemPermissions: 'system.permissions',

  organization: {
    view: 'organization.view',
    create: 'organization.create',
    edit: 'organization.edit',
    delete: 'organization.delete',
    rolesManage: 'system.org_roles.manage',
    vatManage: 'system.org_vat.manage',
  },

  // Stores console (ff-api alembic 0383). Each action is its own code.
  store: {
    view: 'admin.store.view',
    suspend: 'admin.store.suspend',
    verify: 'admin.store.verify',
    feature: 'admin.store.feature',
    publish: 'admin.store.publish',
    review: 'admin.store.review',
    plan: 'admin.store.plan',
    payout: 'admin.store.payout',
    commission: 'admin.store.commission',
    delete: 'admin.store.delete',
    transfer: 'admin.store.transfer',
    settings: 'admin.store.settings',
    permissions: 'admin.store.permissions',
    profile: 'admin.store.profile',
    // Seller-parity writes (ff-api alembic 0460).
    orders: 'admin.store.orders',
    inventory: 'admin.store.inventory',
    pricing: 'admin.store.pricing',
    billing: 'admin.store.billing',
    vouchers: 'admin.store.vouchers',
    messages: 'admin.store.messages',
  },

  // Virtual Tours (Matterport carve-out, ff-api#1374 Phase 1).
  virtualTour: {
    view: 'admin.virtual_tour.view',
    edit: 'admin.virtual_tour.edit',
    link: 'admin.virtual_tour.link',
    publish: 'admin.virtual_tour.publish',
    archive: 'admin.virtual_tour.archive',
    reactivate: 'admin.virtual_tour.reactivate',
    purchase: 'admin.virtual_tour.purchase',
    provider: 'admin.virtual_tour.provider',
    quotaBypass: 'admin.virtual_tour.quota_bypass',
  },

  // Listings live under the `entity` domain.
  entity: {
    view: 'entity.view',
    create: 'entity.create',
    edit: 'entity.edit',
    delete: 'entity.delete',
    approve: 'entity.approve',
  },

  // Listing ownership claims (ff-api alembic 0458).
  claim: { view: 'claim.view', review: 'claim.review' },

  // Protected tag requests (ff-api#902, alembic 0343). Deciding also needs tag.protected.manage.
  tagRequest: { view: 'tag.request.view', decide: 'tag.request.decide' },
  tag: { protectedManage: 'tag.protected.manage' },

  asset: {
    view: 'asset.view',
    upload: 'asset.upload',
    delete: 'asset.delete',
    // ff-api#1526 phase S (alembic 0512)
    run: 'asset.run',
    runPaid: 'asset.run.paid',
    moderate: 'asset.moderate',
    signalsManage: 'asset.signals.manage',
    // phase 7 (alembic 0518)
    update: 'asset.update',
    transfer: 'asset.transfer',
    purge: 'asset.purge',
  },

  promotion: {
    view: 'promotion.view',
    create: 'promotion.create',
    edit: 'promotion.edit',
    delete: 'promotion.delete',
    approve: 'promotion.approve',
  },

  voucher: {
    view: 'voucher.view',
    create: 'voucher.create',
    edit: 'voucher.edit',
    delete: 'voucher.delete',
    revoke: 'voucher.revoke',
  },

  payment: {
    view: 'payment.view',
    edit: 'payment.edit',
    refund: 'payment.refund',
    void: 'payment.void',
    adjust: 'payment.adjust',
    manage: 'payment.manage',
  },

  campaign: {
    view: 'campaign.view',
    create: 'campaign.create',
    send: 'campaign.send',
    segment: 'campaign.segment',
  },

  message: { view: 'message.view' },
  messaging: { view: 'messaging.view', manage: 'messaging.manage' },
  whatsapp: {
    view: 'whatsapp.view',
    send: 'whatsapp.send',
    manageNumbers: 'whatsapp.manage_numbers',
    admin: 'whatsapp.admin',
  },
  marketing: { view: 'marketing.view' },

  cms: {
    view: 'cms.view',
    create: 'cms.create',
    edit: 'cms.edit',
    delete: 'cms.delete',
    publish: 'cms.publish',
  },

  learn: { view: 'learn.view' },
  menu: { view: 'menu.view' },
  report: { view: 'report.view', export: 'report.export' },
  support: { view: 'support.view', manage: 'support.manage' },
  workflow: { view: 'workflow.view', manage: 'workflow.manage' },

  sales: {
    view: 'sales.view',
    prospectWrite: 'sales.prospect.write',
    claimWrite: 'sales.claim.write',
    outreachWrite: 'sales.outreach.write',
    reassign: 'sales.reassign',
    actAs: 'sales.act_as',
    rulesManage: 'sales.rules.manage',
    teamManage: 'sales.team.manage',
    settingsManage: 'sales.settings.manage',
  },

  // Apps catalog (ff-ui header Apps grid + /app/store). write/publish imply
  // read on the API; this UI checks each code as-is.
  apps: {
    catalogRead: 'apps.catalog.read',
    catalogWrite: 'apps.catalog.write',
    catalogPublish: 'apps.catalog.publish',
  },

  integration: {
    view: 'integration.view',
    manage: 'integration.manage',
  },

  // Cronicle job scheduler. operate = run now, pause/resume, abort, acknowledge;
  // manage = create, edit, delete.
  scheduler: {
    view: 'scheduler.view',
    operate: 'scheduler.operate',
    manage: 'scheduler.manage',
  },

  // Hosted services at our cloud provider. logs can hold customer data, so it
  // is its own code; operate = redeploy, restart, cancel, acknowledge;
  // manage = roll back, scale.
  cloud: {
    view: 'cloud.view',
    logs: 'cloud.logs',
    operate: 'cloud.operate',
    manage: 'cloud.manage',
  },

  // Background workers deployed with ff-api. operate = run a task, send a failed
  // run again, drop a task, pause a queue on a worker, restart, acknowledge;
  // manage = purge a queue, rate-limit a task. Logs stay with cloud.logs.
  workers: {
    view: 'workers.view',
    operate: 'workers.operate',
    manage: 'workers.manage',
  },
  // The request limiter: sign-in counters and progressive bans. manage = clear
  // them for a client who is locked out.
  ratelimit: {
    view: 'ratelimit.view',
    manage: 'ratelimit.manage',
  },

  // Voice calling (private calls between buyers and sellers through call boxes).
  // manage = every write; recordings = listen to a call's recording.
  voice: {
    view: 'voice.view',
    manage: 'voice.manage',
    recordings: 'voice.recordings',
  },

  system: {
    featureFlags: 'system.feature_flags.manage',
    highlights: 'system.highlights.manage',
    employees: 'system.employee.manage',
    consoleAdmin: 'system.admin.manage',
    settings: 'system.settings',
    configView: 'system.config.view',
  },
} as const;

// Human-readable labels keyed by code, for button tooltips and the
// access-restricted screen. Falls back to the raw code when missing.
export const PERMISSION_LABELS: Record<string, string> = {
  'report.view': 'View reports',
  'badge.view': 'View badges',
  'seo.view': 'View SEO',
  'seo.manage': 'Run SEO page-health checks',
  'brain.view': 'View Brain',
  'brain.manage': 'Manage Brain controls',
  'accounting.view': 'View Accounting',
  'accounting.manage': 'Manage accounting (bills, expenses, vendors, budgets)',
  'payroll.view': 'View payroll',
  'payroll.manage': 'Run payroll',
  'business.view': 'View Business',
  'escrow.view': 'View Escrow',
  'partner.view': 'View Partners',
  'service_request.view': 'View Service Requests',
  'claim.view': 'View ownership claims',
  'claim.review': 'Review ownership claims',
  'tag.request.view': 'View tag requests',
  'tag.request.decide': 'Decide tag requests',
  'tag.protected.manage': 'Manage protected tags',
  'report.export': 'Export reports',
  'support.view': 'Support access',
  'support.manage': 'Manage support',

  'user.view': 'View users',
  'user.edit': 'Edit users',
  'user.delete': 'Delete / erase users',
  'user.role.assign': 'Assign roles',
  'user.role.revoke': 'Revoke roles',
  'role.create': 'Assign roles',
  'system.permissions': 'Manage permissions',

  'organization.view': 'View organizations',
  'organization.create': 'Create organizations',
  'organization.edit': 'Edit organizations',
  'organization.delete': 'Delete organizations',
  'system.org_roles.manage': 'Manage org roles',
  'system.org_vat.manage': 'Manage org VAT',

  'admin.store.view': 'View stores',
  'admin.store.suspend': 'Suspend / close stores',
  'admin.store.verify': 'Verify stores',
  'admin.store.feature': 'Feature stores',
  'admin.store.publish': 'Publish stores',
  'admin.store.review': 'Review stores & listings',
  'admin.store.plan': 'Manage store plans',
  'admin.store.payout': 'Manage store payouts',
  'admin.store.commission': 'Override store commission',
  'admin.store.delete': 'Delete / ban stores',
  'admin.store.transfer': 'Transfer store ownership',
  'admin.store.settings': 'Manage store settings',
  'admin.store.permissions': 'Manage store staff',
  'admin.store.profile': 'Edit store profile & slug',
  'admin.store.orders': 'Manage store orders, refunds & returns',
  'admin.store.inventory': 'Manage store inventory & listings',
  'admin.store.pricing': 'Edit store prices',
  'admin.store.billing': 'Manage store billing',
  'admin.store.vouchers': 'Manage store vouchers',
  'admin.store.messages': 'Read store messages',

  'admin.virtual_tour.view': 'View virtual tours',
  'admin.virtual_tour.edit': 'Edit / re-sync virtual tours',
  'admin.virtual_tour.link': 'Link virtual tours to listings',
  'admin.virtual_tour.publish': 'Publish virtual tours',
  'admin.virtual_tour.archive': 'Archive virtual tours',
  'admin.virtual_tour.reactivate': 'Reactivate virtual tours',
  'admin.virtual_tour.purchase': 'Purchase virtual tour capacity',
  'admin.virtual_tour.provider': 'Manage virtual tour providers',
  'admin.virtual_tour.quota_bypass': 'Bypass virtual tour quota',

  'entity.view': 'View listings',
  'entity.create': 'Create listings',
  'entity.edit': 'Edit listings',
  'entity.delete': 'Delete listings',
  'entity.approve': 'Approve / moderate listings',

  'asset.view': 'View assets',
  'asset.upload': 'Upload assets',
  'asset.delete': 'Delete assets',
  'asset.run': 'Re-run free image analysis on assets',
  'asset.run.paid': 'Re-run paid image analysis on assets',
  'asset.moderate': 'Override asset signals',
  'asset.signals.manage': 'Manage asset signal definitions and thresholds',
  'asset.update': 'Edit assets (details, roles, privacy, cover, replace, upload for a seller)',
  'asset.transfer': "Move assets to another owner's listing",
  'asset.purge': "Permanently remove deleted assets' files",

  'promotion.view': 'View promotions',
  'promotion.edit': 'Edit promotions',
  'promotion.approve': 'Approve promotions',

  'voucher.view': 'View vouchers',
  'payment.view': 'View payments',
  'payment.refund': 'Issue refunds',
  'payment.void': 'Void payments',

  'campaign.view': 'View campaigns',
  'campaign.send': 'Send campaigns',
  'message.view': 'View messaging',
  'messaging.view': 'View messaging',
  'whatsapp.view': 'View WhatsApp',
  'whatsapp.manage_numbers': 'Manage WhatsApp numbers and lines',
  'whatsapp.send': 'Send WhatsApp messages from linked lines',
  'whatsapp.admin': 'Administer the WhatsApp (Evolution) server',
  'marketing.view': 'View marketing',

  'cms.view': 'View content',
  'cms.publish': 'Publish content',
  'learn.view': 'View Learn',
  'menu.view': 'View navigation',
  'workflow.view': 'View workflows',

  'sales.view': 'View sales',
  'sales.prospect.write': 'Add and edit prospects',
  'sales.claim.write': 'Claim and advance prospects',
  'sales.outreach.write': 'Log and send outreach',
  'sales.reassign': 'Reassign claims',
  'sales.act_as': 'Act as another rep',
  'sales.rules.manage': 'Manage sales rules',
  'sales.team.manage': 'Manage the sales team',
  'sales.settings.manage': 'Configure Sales: channels, senders, quiet hours, per-rep and per-prospect overrides',

  'apps.catalog.read': 'View the apps catalog',
  'apps.catalog.write': 'Edit the apps catalog',
  'apps.catalog.publish': 'Publish catalog apps',

  'system.feature_flags.manage': 'Manage feature flags',
  'system.highlights.manage': 'Manage highlights',
  'system.employee.manage': 'Manage employees',
  'hr.view': 'View HR',
  'hr.manage': 'Manage employees (HR)',
  'hr.leave.approve': 'Approve time off',
  'comp.view': 'View compensation',
  'comp.manage': 'Propose pay changes',
  'comp.approve': 'Approve pay changes',
  'perf.view': 'View performance',
  'perf.manage': 'Run performance reviews',
  'system.admin.manage': 'Grant console access',
  'integration.view': 'View integrations',
  'integration.manage': 'Manage integrations',
  'scheduler.view': 'View scheduled jobs',
  'scheduler.operate': 'Run, pause and abort scheduled jobs',
  'scheduler.manage': 'Create, edit and delete scheduled jobs',
  'cloud.view': 'View cloud resources',
  'cloud.logs': 'Read cloud service logs',
  'cloud.operate': 'Redeploy and restart cloud services',
  'cloud.manage': 'Roll back and scale cloud services',
  'workers.view': 'View background workers',
  'workers.operate': 'Run tasks and restart background workers',
  'workers.manage': 'Purge queues and rate-limit tasks',
  'ratelimit.view': 'View rate limits and bans',
  'ratelimit.manage': 'Clear rate limits and bans',
  'voice.view': 'View voice calls, boxes and announcements',
  'voice.manage': 'Change voice announcements, boxes, modems and settings, and cancel calls',
  'voice.recordings': 'Listen to voice call recordings',
  'system.settings': 'Manage settings',
  'system.config.view': 'View settings',
};

// Friendly label for a code (for tooltips / messages).
export const permissionLabel = (code: string): string =>
  PERMISSION_LABELS[code] ?? code;

// Route → required permission, most-specific first (first match wins, so put
// detail routes above their section root). `null` = explicitly open to any admin.
// Unmapped paths are denied — do not omit a section and expect it to stay open.
//
// UX-only (hide UI; ff-api does not enforce them — any admin=1 caller is accepted):
// user.view/edit/delete, user.role.assign, entity.*, cms.*, organization.*,
// asset.*, claim.*, report.*, menu.view, integration.*, whatsapp.view,
// system.settings, system.feature_flags.manage, admin.store.*.
// Hiding a nav item here is not a security control. role.create matches
// POST /api/admin/user_role. system.permissions matches /admin/users/permissions
// and is held only by administrator_super (ff-api#1286).
type RoutePermission = { pattern: string; code: string | null };

const P = PERMISSIONS;

export const ROUTE_PERMISSIONS: RoutePermission[] = [
  { pattern: '/admin', code: null },
  // Drill-down from the Analytics overview's KPI tiles; same code as its parent.
  { pattern: '/admin/analytics/attempts', code: P.report.view },
  { pattern: '/admin/analytics/visitors', code: P.report.view },
  { pattern: '/admin/analytics', code: P.report.view },
  { pattern: '/admin/integrations/*', code: P.integration.view },
  { pattern: '/admin/integrations', code: P.integration.view },
  { pattern: '/admin/scheduler/jobs/new', code: P.scheduler.manage },
  { pattern: '/admin/scheduler/*', code: P.scheduler.view },
  { pattern: '/admin/scheduler', code: P.scheduler.view },
  { pattern: '/admin/cloud/*', code: P.cloud.view },
  { pattern: '/admin/cloud', code: P.cloud.view },
  { pattern: '/admin/incidents', code: P.system.configView },
  { pattern: '/admin/workers/*', code: P.workers.view },
  { pattern: '/admin/workers', code: P.workers.view },
  { pattern: '/admin/rate-limits/*', code: P.ratelimit.view },
  { pattern: '/admin/rate-limits', code: P.ratelimit.view },
  { pattern: '/admin/support', code: P.support.view },

  // Users & account management.
  { pattern: '/admin/users/roles', code: P.user.roleAssign },
  { pattern: '/admin/users/permissions', code: P.systemPermissions },
  { pattern: '/admin/users/waitlist', code: P.user.view },
  { pattern: '/admin/users/organization/*', code: P.organization.view },
  { pattern: '/admin/users/organization', code: P.organization.view },
  { pattern: '/admin/users/:uid', code: P.user.view },
  { pattern: '/admin/users', code: P.user.view },

  // Organizations.
  { pattern: '/admin/organizations/moderation', code: P.organization.edit },
  { pattern: '/admin/organizations/roles', code: P.organization.rolesManage },
  { pattern: '/admin/organizations/:oid', code: P.organization.view },
  { pattern: '/admin/organizations', code: P.organization.view },

  // Stores. The catalogue paths sit above :storeid so they don't match it.
  { pattern: '/admin/stores/catalog/devices', code: P.store.view },
  { pattern: '/admin/stores/catalog', code: P.store.view },
  { pattern: '/admin/stores/:storeid', code: P.store.view },
  { pattern: '/admin/stores', code: P.store.view },

  // Ownership claims — own code, not entity.view: approving moves a listing to another account.
  { pattern: '/admin/ownership-claims/*', code: P.claim.view },
  { pattern: '/admin/ownership-claims', code: P.claim.view },

  // Protected tag requests — own code, wins over the generic /admin/listings rules below.
  { pattern: '/admin/tag-requests', code: P.tagRequest.view },

  // Verifications — the code its API asks for (/api/admin/verifications).
  { pattern: '/admin/verifications', code: P.entity.view },

  // Virtual Tours — own code, wins over the generic entity.view rule below.
  { pattern: '/admin/listings/virtual-tours/*', code: P.virtualTour.view },
  { pattern: '/admin/listings/virtual-tours', code: P.virtualTour.view },

  // Listings (entity domain).
  { pattern: '/admin/listings/*', code: P.entity.view },
  { pattern: '/admin/listings', code: P.entity.view },

  // Assets.
  { pattern: '/admin/assets/*', code: P.asset.view },
  { pattern: '/admin/assets', code: P.asset.view },

  // Promotions.
  { pattern: '/admin/promotions/*', code: P.promotion.view },
  { pattern: '/admin/promotions', code: P.promotion.view },
  { pattern: '/admin/highlights', code: P.system.highlights },

  // Vouchers.
  { pattern: '/admin/vouchers', code: P.voucher.view },

  // Payments.
  { pattern: '/admin/payments/*', code: P.payment.view },
  { pattern: '/admin/payments', code: P.payment.view },

  // Communications.
  { pattern: '/admin/communications/whatsapp/*', code: P.whatsapp.view },
  // Voice calling; must stay above the communications catch-all. Writes are gated in-page.
  { pattern: '/admin/communications/calling/*', code: P.voice.view },
  { pattern: '/admin/communications/calling', code: P.voice.view },
  { pattern: '/admin/communications/*', code: P.message.view },

  // Campaigns / relationship management.
  { pattern: '/admin/relationship/whatsapp/*', code: P.whatsapp.view },
  { pattern: '/admin/relationship/*', code: P.campaign.view },

  // Marketing / media.
  { pattern: '/admin/marketing/*', code: P.marketing.view },
  { pattern: '/admin/media/*', code: P.marketing.view },
  // SEO has its own codes (ff-api business migration 0494).
  { pattern: '/admin/seo/*', code: 'seo.view' },
  { pattern: '/admin/seo', code: 'seo.view' },

  // Navigation.
  { pattern: '/admin/navigation', code: P.menu.view },

  // Content / CMS (Learn is its own domain).
  { pattern: '/admin/content/learn/*', code: P.learn.view },
  { pattern: '/admin/content/*', code: P.cms.view },

  // Apps catalog. Edits and status changes are gated per-button.
  { pattern: '/admin/apps-catalog/apps/new', code: P.apps.catalogWrite },
  { pattern: '/admin/apps-catalog/*', code: P.apps.catalogRead },
  { pattern: '/admin/apps-catalog', code: P.apps.catalogRead },

  // HR.
  // My workplace: every console user's own record — the API scopes
  // /workplace/* to the session.
  { pattern: '/admin/workplace', code: null },
  // Tasks: every console user files, picks up and closes work.
  { pattern: '/admin/tasks/*', code: null },
  { pattern: '/admin/tasks', code: null },
  { pattern: '/admin/human-resource/me', code: null },
  // Pay is never behind hr.* — comp.* only.
  { pattern: '/admin/human-resource/compensation', code: P.comp.view },
  { pattern: '/admin/human-resource/performance/*', code: P.perf.view },
  { pattern: '/admin/human-resource/*', code: P.hr.view },
  { pattern: '/admin/human-resource', code: P.hr.view },

  // Workflows.
  { pattern: '/admin/workflows/*', code: P.workflow.view },
  { pattern: '/admin/workflows', code: P.workflow.view },

  // Feature flags.
  { pattern: '/admin/features/*', code: P.system.featureFlags },
  { pattern: '/admin/features', code: P.system.featureFlags },
  { pattern: '/admin/feature-flags', code: P.system.featureFlags },

  // Settings.
  { pattern: '/admin/settings/*', code: P.system.configView },

  { pattern: '/admin/sales/settings', code: P.sales.settingsManage },
  { pattern: '/admin/sales/*', code: P.sales.view },
  { pattern: '/admin/sales', code: P.sales.view },

  // Badges.
  { pattern: '/admin/badges/*', code: 'badge.view' },
  { pattern: '/admin/badges', code: 'badge.view' },

  // Brain (AI controls and knowledge base).
  { pattern: '/admin/ai/controls', code: 'brain.manage' },
  { pattern: '/admin/ai/*', code: 'brain.view' },
  { pattern: '/admin/ai', code: 'brain.view' },

  // Sections with a code of their own (ff-api business migration 0493).
  { pattern: '/admin/service-requests/*', code: 'service_request.view' },
  { pattern: '/admin/service-requests', code: 'service_request.view' },
  // Org structure lives under Business but is HR data (/api/admin/v2/hr).
  { pattern: '/admin/business/departments', code: P.hr.view },
  { pattern: '/admin/business/levels', code: P.hr.view },
  // Spend lives under Business but is accounting data (/api/admin/v2/business).
  { pattern: '/admin/business/contracts', code: P.accounting.view },
  { pattern: '/admin/business/recurring', code: P.accounting.view },
  { pattern: '/admin/business/expense-types', code: P.accounting.view },
  { pattern: '/admin/business/compliance', code: P.accounting.view },
  { pattern: '/admin/business/assets', code: P.accounting.view },
  { pattern: '/admin/business/purchases', code: P.accounting.view },
  { pattern: '/admin/business/*', code: 'business.view' },
  { pattern: '/admin/business', code: 'business.view' },
  // Payroll shows everyone's pay: payroll.* only, never accounting.view.
  { pattern: '/admin/accounting/payroll/*', code: P.payroll.view },
  { pattern: '/admin/accounting/*', code: 'accounting.view' },
  { pattern: '/admin/accounting', code: 'accounting.view' },
  // Partners lives under the escrow path but is its own menu.
  { pattern: '/admin/escrow/providers', code: 'partner.view' },
  { pattern: '/admin/escrow/*', code: 'escrow.view' },
  { pattern: '/admin/escrow', code: 'escrow.view' },
];

// Permission code required to view `pathname`: a code when gated, `null` when
// explicitly open, `undefined` when no rule matches (callers deny that).
export const resolvePermissionForPath = (
  pathname: string
): string | null | undefined => {
  for (const { pattern, code } of ROUTE_PERMISSIONS) {
    if (matchPath({ path: pattern, end: !pattern.endsWith('/*') }, pathname)) {
      return code;
    }
  }
  return undefined;
};
